import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { autorun } from 'mobx'
import { trackWatchFolderSelected, trackAutoSyncFolderStopped } from 'src/services/tracker/trackers/uploads'
import type { ThunkDispatch } from 'redux-thunk'
import type { AnyAction } from 'redux'
import {
	createFileUploads, removeAssetUploads, updateAssetUploadFile,
	renameUploadedAsset, checkAssetFilenamesMatches
} from 'src/actions/assetUploads'
import type { SyncedFile } from 'src/new_arch/stores/FolderSyncStore'
import type FolderSyncStore from 'src/new_arch/stores/FolderSyncStore'
import FilenamesMatchResult from '@uploads/models/FilenamesMatchResult'
import { ASSET_ALL_EXTENSIONS } from 'src/entities/asset'
import { useApi } from 'src/new_arch/providers/ApiProvider'
import { PROJECTS_URL } from 'src/config/urls'
import { getRawAssetUploads } from 'src/selectors/assetUploads'
import { ITEM_UPLOAD_STATUS } from 'src/services/DirectFilesUploaderCoordinator'
import { useRootStore } from 'src/new_arch/providers/RootStoreProvider'

export type { SyncedFile } from 'src/new_arch/stores/FolderSyncStore'

type FSDirectoryHandle = FileSystemDirectoryHandle & {
	values(): AsyncIterableIterator<FileSystemFileHandle | FileSystemDirectoryHandle>
}
type WindowWithFSA = {
	showDirectoryPicker( options?: { mode?: 'read' | 'readwrite' } ): Promise<FSDirectoryHandle>
}

type UseFolderSyncParams = {
	projectID: number
	folderID: number | null
}

type UseFolderSyncResult = {
	isBrowserSupported: boolean
	isWatching: boolean
	folderName: string | null
	syncedFiles: SyncedFile[]
	startWatching: () => Promise<boolean>
	stopWatching: () => void
}

type Fingerprint = string

type TrackedEntry = {
	filename: string
	uploadID: string | number
	size: number
	lastModified: number
	pendingRename?: string
}

const POLL_INTERVAL_MS = 15_000
const STABILITY_CHECK_DELAY_MS = 500

const isFolderSyncSupported = () => 'showDirectoryPicker' in window

const isAllowedExtension = ( filename: string ) => {
	const ext = filename.split( '.' ).pop()?.toLowerCase()
	return !!ext && ASSET_ALL_EXTENSIONS.includes( ext )
}

const fingerprint = ( size: number, lastModified: number ): Fingerprint => `${size}:${lastModified}`

const collectHandles = async (
	dirHandle: FSDirectoryHandle
): Promise<FileSystemFileHandle[]> => {
	const iter = dirHandle.values()
	const drain = async ( acc: FileSystemFileHandle[] ): Promise<FileSystemFileHandle[]> => {
		const { done, value } = await iter.next()
		if ( done ) return acc
		if ( value.kind === 'file' ) acc.push( value as FileSystemFileHandle )
		return drain( acc )
	}
	return drain( [] )
}

const isFileStable = async ( handle: FileSystemFileHandle, knownSize: number ): Promise<boolean> => {
	await new Promise( resolve => setTimeout( resolve, STABILITY_CHECK_DELAY_MS ) )
	return ( await handle.getFile() ).size === knownSize
}

const getTracked = ( store: FolderSyncStore ) => store.trackedMap as Map<string | number, TrackedEntry>
const getSeenFilenames = ( store: FolderSyncStore ) => store.seenFilenamesMap as Map<string, Fingerprint | null>
const getActiveDirHandle = ( store: FolderSyncStore ) => store.activeDirHandle as FSDirectoryHandle | null
type AssetUploadsMap = ReturnType<typeof getRawAssetUploads> | null
const getAssetUploads = ( store: FolderSyncStore ) => store.assetUploadsCache as AssetUploadsMap

const useFolderSync = ( { projectID, folderID }: UseFolderSyncParams ): UseFolderSyncResult => {
	const dispatch = useDispatch<ThunkDispatch<unknown, unknown, AnyAction>>()
	const api = useApi()
	const { folderSyncStore } = useRootStore()

	const [ isWatching, setIsWatching ] = useState( () => folderSyncStore.isWatching )
	const [ folderName, setFolderName ] = useState( () => folderSyncStore.folderName )
	const [ syncedFiles, setSyncedFiles ] = useState<SyncedFile[]>( () => [ ...folderSyncStore.syncedFiles ] )

	useEffect( () => autorun( () => {
		setIsWatching( folderSyncStore.isWatching )
		setFolderName( folderSyncStore.folderName )
		setSyncedFiles( [ ...folderSyncStore.syncedFiles ] )
	} ), [] )

	const reduxAssetUploads = useSelector( getRawAssetUploads )
	const reduxAssetUploadsRef = useRef( reduxAssetUploads )
	useEffect( () => {
		reduxAssetUploadsRef.current = reduxAssetUploads
		folderSyncStore.assetUploadsCache = reduxAssetUploads
	}, [ reduxAssetUploads ] )

	const isBrowserSupported = isFolderSyncSupported()

	const scanAndUpload = useCallback( async () => {
		const activeHandle = getActiveDirHandle( folderSyncStore )
		if ( !activeHandle ) return

		Array.from( getTracked( folderSyncStore ).entries() ).forEach( ( [ uploadID, entry ] ) => {
			const upload = getAssetUploads( folderSyncStore )?.get( uploadID )
			if ( upload?.status === ITEM_UPLOAD_STATUS.FAILED ) {
				getTracked( folderSyncStore ).delete( uploadID )
				getSeenFilenames( folderSyncStore ).delete( entry.filename )
				folderSyncStore.updateSyncedFiles( prev => prev.filter( f => f.filename !== entry.filename ) )
				dispatch( removeAssetUploads( [ uploadID ] ) )
			}
		} )

		const allHandles = await collectHandles( activeHandle )
		const allowedHandles = allHandles.filter( h => isAllowedExtension( h.name ) )

		const trackedFilenames = new Set(
			Array.from( getTracked( folderSyncStore ).values() ).map( e => e.filename )
		)
		const fetchedFiles = await Promise.all( allowedHandles.map( h => h.getFile() ) )

		const currentByFilename = new Map<string, File>()
		const currentByFingerprint = new Map<Fingerprint, File>()
		fetchedFiles.forEach( ( f ) => {
			currentByFilename.set( f.name, f )
			currentByFingerprint.set( fingerprint( f.size, f.lastModified ), f )
		} )

		getSeenFilenames( folderSyncStore ).forEach( ( seenFP, name ) => {
			if ( trackedFilenames.has( name ) ) return
			const current = currentByFilename.get( name )
			if ( !current ) return
			const currentFP = fingerprint( current.size, current.lastModified )
			if ( seenFP === null ) {
				getSeenFilenames( folderSyncStore ).set( name, currentFP )
			} else if ( currentFP !== seenFP ) {
				getSeenFilenames( folderSyncStore ).delete( name )
			}
		} )

		const unmatchedFilenames = new Set<string>( allowedHandles.map( h => h.name ) )
		const usedFingerprints = new Set<Fingerprint>()

		Array.from( getTracked( folderSyncStore ).entries() ).forEach( ( [ uploadID, entry ] ) => {
			const fp = fingerprint( entry.size, entry.lastModified )
			const byName = currentByFilename.get( entry.filename )

			if ( byName ) {
				if ( byName.lastModified !== entry.lastModified ) {
					getTracked( folderSyncStore ).delete( uploadID )
					getSeenFilenames( folderSyncStore ).delete( entry.filename )
				} else {
					unmatchedFilenames.delete( entry.filename )
				}
			} else {
				const byFP = currentByFingerprint.get( fp )
				const isRename = byFP && unmatchedFilenames.has( byFP.name ) && !usedFingerprints.has( fp )

				if ( isRename ) {
					const newFilename = byFP.name
					usedFingerprints.add( fp )
					unmatchedFilenames.delete( newFilename )
					getTracked( folderSyncStore ).set( uploadID, {
						...entry, filename: newFilename, pendingRename: newFilename
					} )
					dispatch( updateAssetUploadFile( uploadID, byFP ) )
					folderSyncStore.updateSyncedFiles( prev => prev.map(
						f => ( f.filename === entry.filename ? { ...f, filename: newFilename } : f )
					) )
					const currentFolderName = getActiveDirHandle( folderSyncStore )?.name
					if ( currentFolderName ) {
						getSeenFilenames( folderSyncStore ).set(
							newFilename, fingerprint( byFP.size, byFP.lastModified )
						)
						api.post(
							`${PROJECTS_URL}/${projectID}/folder_sync/register`,
							{ folder_name: currentFolderName, folder_id: folderID ?? 0, filenames: [ newFilename ] }
						).catch( () => { /* non-critical */ } )
					}
				} else {
					getTracked( folderSyncStore ).delete( uploadID )
					dispatch( removeAssetUploads( [ uploadID ] ) )
					folderSyncStore.updateSyncedFiles( prev => prev.filter( f => f.filename !== entry.filename ) )
				}
			}
		} )

		const newHandles = allowedHandles.filter(
			h => unmatchedFilenames.has( h.name ) && !getSeenFilenames( folderSyncStore ).has( h.name )
		)
		const stableNewHandles = await Promise.all(
			newHandles.map( async ( fileHandle ) => {
				const file = currentByFilename.get( fileHandle.name )!
				return { fileHandle, file, stable: await isFileStable( fileHandle, file.size ) }
			} )
		).then( results => results.filter( r => r.stable ) )

		if ( stableNewHandles.length > 0 ) {
			const files = stableNewHandles.map( e => e.file )

			const localFolderName = getActiveDirHandle( folderSyncStore )?.name
			const matchDispatch = dispatch(
				checkAssetFilenamesMatches( { projectID, folderID, files, localFolderName } )
			)
			const matchAction = await ( matchDispatch as Promise<{ payload: FilenamesMatchResult }> )
			const newRounds = ( matchAction?.payload?.exactMatches ?? [] ).map(
				m => ( { filename: m.filename, assetID: m.match.id } )
			)

			const action = dispatch( createFileUploads( {
				projectID, folderID, parentUploadID: undefined, files, newRounds, localFolderName
			} ) )
			const payload: Array<{ id: string | number }> = action?.payload ?? []

			stableNewHandles.forEach( ( { file }, index ) => {
				const uploadID = payload[ index ]?.id
				if ( uploadID == null ) return
				getTracked( folderSyncStore ).set( uploadID, {
					filename: file.name, uploadID, size: file.size, lastModified: file.lastModified
				} )
				folderSyncStore.updateSyncedFiles( prev => [
					...prev, { filename: file.name, detectedAt: new Date() }
				] )
			} )

			const uploadedFilenames = stableNewHandles.map( e => e.file.name )
			const currentFolderName = getActiveDirHandle( folderSyncStore )?.name
			if ( currentFolderName ) {
				api.post(
					`${PROJECTS_URL}/${projectID}/folder_sync/register`,
					{ folder_name: currentFolderName, folder_id: folderID ?? 0, filenames: uploadedFilenames }
				).then( () => {
					stableNewHandles.forEach( ( { file } ) => {
						getSeenFilenames( folderSyncStore ).set(
							file.name, fingerprint( file.size, file.lastModified )
						)
					} )
				} ).catch( () => { /* non-critical */ } )
			}
		}

		Array.from( getTracked( folderSyncStore ).entries() )
			.filter( ( [ , entry ] ) => !!entry.pendingRename )
			.forEach( ( [ uploadID, entry ] ) => {
				( dispatch( renameUploadedAsset( uploadID, entry.pendingRename! ) ) as Promise<boolean> )
					.then( ( renamed ) => {
						if ( !renamed ) return
						const current = getTracked( folderSyncStore ).get( uploadID )
						if ( current ) {
							getTracked( folderSyncStore ).set( uploadID, { ...current, pendingRename: undefined } )
						}
					} )
			} )
	}, [ dispatch, folderSyncStore, projectID, folderID ] )

	const clearTrackedUploads = useCallback( () => {
		if ( getTracked( folderSyncStore ).size === 0 ) return
		const ids = [ ...getTracked( folderSyncStore ).values() ].map( e => e.uploadID )
		dispatch( removeAssetUploads( ids ) )
		folderSyncStore.trackedMap = new Map()
	}, [ dispatch, folderSyncStore ] )

	const stopWatching = useCallback( () => {
		if ( folderSyncStore.activeDirHandle ) {
			trackAutoSyncFolderStopped( { assetsSynced: folderSyncStore.syncedFiles.length } )
		}
		clearTrackedUploads()
		if ( folderSyncStore.intervalHandle ) {
			clearInterval( folderSyncStore.intervalHandle )
			folderSyncStore.intervalHandle = null
		}
		folderSyncStore.activeDirHandle = null
		folderSyncStore.seenFilenamesMap = new Map()
		folderSyncStore.setIsWatching( false )
		folderSyncStore.setFolderName( null )
		folderSyncStore.setSyncedFiles( [] )
	}, [ clearTrackedUploads, folderSyncStore ] )

	const startWatching = useCallback( async (): Promise<boolean> => {
		if ( !isFolderSyncSupported() ) return false

		try {
			if ( folderSyncStore.intervalHandle ) {
				clearInterval( folderSyncStore.intervalHandle )
				folderSyncStore.intervalHandle = null
			}
			clearTrackedUploads()

			const handle = await ( window as unknown as WindowWithFSA ).showDirectoryPicker( { mode: 'read' } )
			folderSyncStore.activeDirHandle = handle
			folderSyncStore.trackedMap = new Map()
			folderSyncStore.setSyncedFiles( [] )
			folderSyncStore.setFolderName( handle.name )
			folderSyncStore.setIsWatching( true )
			trackWatchFolderSelected( { folderName: handle.name } )

			const params = new URLSearchParams( {
				folder_name: handle.name,
				folder_id: String( folderID ?? 0 )
			} )
			try {
				const response = await api.get( `${PROJECTS_URL}/${projectID}/folder_sync?${params}` )
				const { synced_filenames: alreadySynced = [] } = response as unknown as {
					synced_filenames: string[]
				}
				folderSyncStore.seenFilenamesMap = new Map( alreadySynced.map( name => [ name, null ] ) )
			} catch {
				folderSyncStore.seenFilenamesMap = new Map()
			}

			await scanAndUpload()
			folderSyncStore.intervalHandle = setInterval( scanAndUpload, POLL_INTERVAL_MS )
			return true
		} catch ( err ) {
			if ( !( err instanceof DOMException && err.name === 'AbortError' ) ) {
				throw err
			}
			return false
		}
	}, [ scanAndUpload, clearTrackedUploads, api, projectID, folderID, folderSyncStore ] )

	useEffect( () => {
		if ( !isWatching ) return () => {}
		const handleBeforeUnload = ( e: BeforeUnloadEvent ) => {
			e.preventDefault()
			e.returnValue = ''
		}
		window.addEventListener( 'beforeunload', handleBeforeUnload )
		return () => window.removeEventListener( 'beforeunload', handleBeforeUnload )
	}, [ isWatching ] )

	useEffect( () => () => {
		if ( folderSyncStore.isWatching ) return
		if ( getTracked( folderSyncStore ).size > 0 ) {
			const ids = [ ...getTracked( folderSyncStore ).values() ].map( e => e.uploadID )
			dispatch( removeAssetUploads( ids ) )
		}
	}, [] )

	return {
		isBrowserSupported,
		isWatching,
		folderName,
		syncedFiles,
		startWatching,
		stopWatching
	}
}

export default useFolderSync
